/**
 * チェック項目管理サービス（実API実装）
 *
 * バックエンドAPI呼び出し:
 * - GET /api/check-categories - チェックカテゴリ一覧取得
 * - GET /api/check-items - チェック項目一覧取得
 * - POST /api/check-items - チェック項目作成（管理者）
 * - PUT /api/check-items/{id} - チェック項目更新（管理者）
 * - DELETE /api/check-items/{id} - チェック項目削除（管理者）
 */
import apiClient, { extractErrorMessage } from '../../lib/apiClient';
import type { CheckItem, RiskCategory, MAMethod } from '../../types';

// ===== サービス固有の型定義 =====

/**
 * カテゴリ情報（カテゴリ一覧取得用）
 */
export interface CategoryInfo {
  id: string;
  name: string;
  riskCategory: RiskCategory;
  description?: string;
  order: number;
  itemCount: number;
}

/**
 * チェック項目作成リクエスト
 */
export interface CreateCheckItemRequest {
  categoryId: string;
  name: string;
  description?: string;
  maMethod: MAMethod | 'all';
  priority: 'high' | 'medium' | 'low';
}

/**
 * チェック項目更新リクエスト
 */
export interface UpdateCheckItemRequest {
  name?: string;
  description?: string;
  maMethod?: MAMethod | 'all';
  priority?: 'high' | 'medium' | 'low';
}

/**
 * チェック項目取得時のフィルタ条件
 */
export interface GetCheckItemsParams {
  categoryId?: string;
  maMethod?: MAMethod | 'all';
  priority?: 'high' | 'medium' | 'low';
}

// ===== バックエンドレスポンス型（camelCase - バックエンドがcamelCaseで返すため） =====

interface BackendCategoryInfo {
  id: string;
  name: string;
  riskCategory: RiskCategory;
  description?: string;
  displayOrder: number;
  itemCount: number;
}

interface BackendCheckItem {
  id: string;
  categoryId: string;
  name: string;
  description?: string;
  targetMaMethods: MAMethod[];
  priority: 'high' | 'medium' | 'low';
  displayOrder: number;
  isCustom?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// ===== 変換関数（バックエンドがcamelCaseで返すため、フィールド名変換のみ） =====

/**
 * バックエンドのカテゴリ情報をフロントエンドの型に変換
 */
function convertCategoryInfo(backendCategory: BackendCategoryInfo): CategoryInfo {
  return {
    id: backendCategory.id,
    name: backendCategory.name,
    riskCategory: backendCategory.riskCategory,
    description: backendCategory.description,
    order: backendCategory.displayOrder,
    itemCount: backendCategory.itemCount,
  };
}

/**
 * バックエンドのチェック項目をフロントエンドの型に変換
 */
function convertCheckItem(backendItem: BackendCheckItem): CheckItem {
  // targetMaMethodsが空または5つ全てなら'all'、それ以外は最初の要素を使用
  const allMethods: MAMethod[] = ['stock_transfer', 'business_transfer', 'merger', 'company_split', 'stock_exchange'];
  const maMethod: MAMethod | 'all' =
    backendItem.targetMaMethods.length === 0 || backendItem.targetMaMethods.length >= allMethods.length
      ? 'all'
      : backendItem.targetMaMethods[0];

  return {
    id: backendItem.id,
    categoryId: backendItem.categoryId,
    name: backendItem.name,
    description: backendItem.description,
    maMethod,
    priority: backendItem.priority,
    order: backendItem.displayOrder,
  };
}

// ===== チェック項目サービス =====

export const checkItemService = {
  /**
   * チェックカテゴリ一覧取得
   * GET /api/check-categories
   */
  async getCategories(): Promise<CategoryInfo[]> {
    try {
      const response = await apiClient.get<{ data: BackendCategoryInfo[] }>(
        '/api/check-categories'
      );
      const data = response.data?.data || response.data || [];
      if (!Array.isArray(data)) return [];
      return data.map(convertCategoryInfo);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * チェック項目一覧取得
   * GET /api/check-items
   *
   * @param params - フィルタ条件（オプション）
   */
  async getCheckItems(params?: GetCheckItemsParams): Promise<CheckItem[]> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.categoryId) {
        queryParams.append('category_id', params.categoryId);
      }
      if (params?.maMethod) {
        queryParams.append('ma_method', params.maMethod);
      }
      if (params?.priority) {
        queryParams.append('priority', params.priority);
      }

      const queryString = queryParams.toString();
      const url = queryString ? `/api/check-items?${queryString}` : '/api/check-items';

      const response = await apiClient.get<{ data: BackendCheckItem[] }>(url);
      const data = response.data?.data || response.data || [];
      if (!Array.isArray(data)) return [];
      return data.map(convertCheckItem);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * カテゴリ別チェック項目取得（便利メソッド）
   * GET /api/check-items?category_id=xxx
   *
   * @param categoryId - カテゴリID
   */
  async getCheckItemsByCategory(categoryId: string): Promise<CheckItem[]> {
    return this.getCheckItems({ categoryId });
  },

  /**
   * チェック項目作成（管理者用）
   * POST /api/check-items
   *
   * @param data - 作成するチェック項目データ
   */
  async createCheckItem(data: CreateCheckItemRequest): Promise<CheckItem> {
    try {
      const response = await apiClient.post<BackendCheckItem>('/api/check-items', {
        category_id: data.categoryId,
        name: data.name,
        description: data.description,
        ma_method: data.maMethod,
        priority: data.priority,
      });
      return convertCheckItem(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * チェック項目更新（管理者用）
   * PUT /api/check-items/{id}
   *
   * @param id - チェック項目ID
   * @param data - 更新するデータ
   */
  async updateCheckItem(id: string, data: UpdateCheckItemRequest): Promise<CheckItem> {
    try {
      const requestData: Record<string, unknown> = {};

      if (data.name !== undefined) {
        requestData.name = data.name;
      }
      if (data.description !== undefined) {
        requestData.description = data.description;
      }
      if (data.maMethod !== undefined) {
        requestData.ma_method = data.maMethod;
      }
      if (data.priority !== undefined) {
        requestData.priority = data.priority;
      }

      const response = await apiClient.put<BackendCheckItem>(
        `/api/check-items/${id}`,
        requestData
      );
      return convertCheckItem(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * チェック項目削除（管理者用）
   * DELETE /api/check-items/{id}
   *
   * @param id - 削除するチェック項目ID
   */
  async deleteCheckItem(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/check-items/${id}`);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },
};

export default checkItemService;
